// src/pages/SummaryIndomaretRegistration.jsx
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  RadioGroup,
  Radio,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import api from '../../services/apiClient';
import RegStatusDialog from '../RegCompletePopup/RegStatusDialog';

const RADIUS_PX = 20;
const SECTION_BORDER = '#E5E7EB';

const DROP_BAG_TOC_TEXT = `<ul>
  <li>No valuables (organizer is not responsible for loss)</li>
  <li>Operating hours: Race Day only</li>
  <li>Claim with bib/wristband &amp; keep your receipt</li>
</ul>`;

const HOME_PATH =
  '/event/ZXlKcFpDSTZPRGtzSW5SNWNHVWlPaUpQUmtaTVNVNUZJbjA9K2lyYWNlMjAyMA==';

const TERMS_TOC_TEXT = `...`; // (keep your long HTML text as-is)
const WAIVER_TOC_TEXT = `...`; // (keep your long HTML text as-is)
const PRIVACY_TOC_TEXT = `...`; // (keep your long HTML text as-is)
const RULES_TOC_TEXT = `...`; // (keep your long HTML text as-is)

// helpers
const pad2 = (n) => String(n ?? 0).padStart(2, '0');
const fmtTime = (t) =>
  t && typeof t === 'object'
    ? `${pad2(t.hour)}:${pad2(t.minute)}:${pad2(t.second)}`
    : t || '—';

const show = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') {
    if ('label' in v) return v.label ?? v.value ?? '—';
    if ('value' in v) return v.value ?? '—';
  }
  return String(v);
};

const normalizeRace = (race) => {
  const s = String(race || '').toLowerCase();
  if (s.includes('21')) return '21k';
  if (s.includes('10')) return '10k';
  if (s.includes('5')) return '5k';
  return s || '10k';
};

const showSizeValue = (size) => {
  if (!size) return '—';
  if (typeof size === 'object') return size.value || size.label || '—';
  return String(size).split(' - ')[0];
};

function pad(num) {
  return String(num).padStart(2, '0');
}

function normalizeRegistration(input) {
  const output = {};

  for (const [key, value] of Object.entries(input)) {
    if (key === 'org_kom') continue;

    if (
      key === 'gender' &&
      value &&
      typeof value === 'object' &&
      'value' in value
    ) {
      output[key] = value.value ?? '-';
      continue;
    }

    if (key === 'org_com') {
      output[key] = input.org_kom === 'yes' ? (value ?? '-') : '-';
      continue;
    }

    if (value instanceof File || value instanceof Blob) {
      output[key] = value;
      continue;
    }

    if (value == null) {
      output[key] = '-';
      continue;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      if (
        ['size', 'belt_size', 'finisher_size'].includes(key) &&
        'value' in value
      ) {
        output[key] = value.value ?? '-';
      } else if ('hour' in value && 'minute' in value && 'second' in value) {
        output[key] = `${pad(value.hour)}:${pad(value.minute)}:${pad(
          value.second,
        )}`;
      } else if ('label' in value) {
        output[key] = value.label ?? '-';
      } else {
        output[key] = '-';
      }
      continue;
    }

    output[key] = value;
  }

  return output;
}

function Section({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${RADIUS_PX}px`,
        borderColor: SECTION_BORDER,
        borderWidth: 2,
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          mb: 1.5,
          fontSize: 'clamp(1.25rem, 0.55vw + 1rem, 1.5rem)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function KV({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TocDialog({ open, onClose, title, text }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: text,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const postRequest = (type, data, options = {}) => {
  if (parseInt(type) === 231) {
    return api.post('/indomaret/event/register', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // baseURL: `${process.env.REACT_APP_V1_API_URL}/api`,
    });
  } else {
    return api.post('/indomaret/event/register', data);
  }
};

export default function SummaryIndomaretRegistration() {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();

  const [registeraLoading, setRegisteraLoading] = useState(false);
  const [tocModal, setTocModal] = useState({
    open: false,
    title: '',
    text: '',
  });
  const [resultDialog, setResultDialog] = useState({
    open: false,
    status: 'success',
    title: '',
    message: '',
  });
  const openToc = (title, text) => setTocModal({ open: true, title, text });
  const closeToc = () => setTocModal((p) => ({ ...p, open: false }));

  // Data dari form
  const values = state?.values || null;
  const meta = state?.meta || null;

  // path balik form
  const registrationPath =
    pathname && /\/summary\/?$/.test(pathname)
      ? pathname.replace(/\/summary\/?$/, '')
      : '/event/register/indomaret';

  // Normalisasi untuk rule UI
  const catEnum = normalizeRace(values?.race_category);
  const orgKom = String(values?.org_kom || '').toLowerCase();
  const is21kCompetitive =
    catEnum === '21k' &&
    (values?.rpt_21?.value || values?.rpt_21) === 'competitive';

  // === RHF untuk Agreement + (opsional) Proof of Run ===
  const {
    control,
    handleSubmit,
    watch,
    setError, // <-- NEW
    clearErrors, // <-- NEW
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      proof_file: null, // File
      agree_terms: false,
      agree_waiver: false,
      agree_privacy: false,
      agree_rules: false,
      drop_bag: '',
    },
  });

  // file validator (shared)
  const validateProof = (f) => {
    const MAX = 2 * 1024 * 1024; // 2MB
    if (!f) return 'Please upload your proof of run (image).';
    if (!(f instanceof File)) return 'Invalid file.';
    if (f.size > MAX) return 'Max file size is 2MB.';
    if (!/^image\//.test(f.type)) return 'File must be an image.';
    if (f.size === 0) return 'File is empty or unreadable.';
    return true;
  };

  const proofFile = watch('proof_file');
  const previewUrl = useMemo(
    () => (proofFile ? URL.createObjectURL(proofFile) : null),
    [proofFile],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Rows (compact)
  const rows = useMemo(() => {
    const arr = [
      { label: 'Email', val: values?.email },
      { label: 'Race Category', val: show(values?.race_category) },
      { label: 'Name', val: values?.name },
      { label: 'Phone Number', val: values?.contact },
      { label: 'Gender', val: show(values?.gender) },
      { label: 'Nationality', val: show(values?.nationality) },
      { label: 'Address', val: show(values?.address) },
      { label: 'Province', val: show(values?.province) },
      {
        label: 'City / Regency',
        val: show(values?.city_regency),
      },
      { label: 'ID Type', val: show(values?.id_type) },
      { label: 'ID Number', val: values?.id_number },
      { label: 'Birth Date', val: values?.brith_date },
      { label: 'Blood Type', val: show(values?.blood_type) },
      ...(orgKom === 'yes' && String(values?.org_com || '').trim()
        ? [{ label: 'Club Name', val: values?.org_com }]
        : []),
      ...(catEnum === '21k' && values?.rpt_21
        ? [{ label: 'Race Prediction Time', val: show(values?.rpt_21) }]
        : []),
      ...(catEnum === '10k' && values?.rpt_10
        ? [{ label: 'Race Prediction Time', val: show(values?.rpt_10) }]
        : []),
      { label: 'Expected Finish Time', val: fmtTime(values?.exp_fin_time) },
      { label: 'Medical Info', val: values?.medical_note },
      { label: 'Jersey Size', val: showSizeValue(values?.size) },
      { label: 'Belt Size', val: show(values?.belt_size) },
      ...(catEnum === '21k' && values?.finisher_size
        ? [
            {
              label: 'Finisher Jacket Size',
              val: show(values?.finisher_size),
            },
          ]
        : []),
      { label: 'Emergency Name', val: values?.emergency_person },
      { label: 'Emergency Contact', val: values?.emergency_contact },
      { label: 'Emergency Relation', val: show(values?.emergency_relation) },
    ];
    return arr;
  }, [values, orgKom, catEnum]);

  // Grid
  const RowGrid = ({ rows }) => (
    <Grid container spacing={1.25}>
      {rows.map((r, i) => (
        <Grid item key={`${r.label}-${i}`} xs={12} md={r.span === 12 ? 12 : 6}>
          <KV label={r.label} value={r.val ?? '—'} />
        </Grid>
      ))}
    </Grid>
  );

  const onEdit = () => {
    navigate(registrationPath, {
      replace: true,
      state: { initialValues: values, meta },
    });
  };

  // SUBMIT: original
  const onSubmit = async (agreements) => {
    const normalizedValues = normalizeRegistration(values);
    const payloadObject = {
      runner: normalizedValues,
      meta,
      agreements: {
        agree_terms: !!agreements.agree_terms,
        agree_waiver: !!agreements.agree_waiver,
        agree_privacy: !!agreements.agree_privacy,
        agree_rules: !!agreements.agree_rules,
        drop_bag_use: agreements.drop_bag === 'use',
      },
    };

    // --- real submit (unchanged) ---
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payloadObject));
    if (is21kCompetitive && agreements.proof_file instanceof File) {
      fd.append(
        'proof_file',
        agreements.proof_file,
        agreements.proof_file.name,
      );
    }

    try {
      setRegisteraLoading(true);

      await postRequest(meta?.category_id, fd);

      setResultDialog({
        open: true,
        status: 'success',
        title: 'Registration Successful',
        message:
          'Your registration was successful.\nE-ticket will be sent via email.',
      });
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        'Submit failed';

      const message =
        typeof rawMessage === 'string'
          ? rawMessage
          : JSON.stringify(rawMessage);

      setResultDialog({
        open: true,
        status: 'error',
        title: 'Registration Failed',
        message,
      });
    } finally {
      setRegisteraLoading(false);
    }
  };

  // PRE-SUBMIT GUARD: ensure proof_file valid & show error near filename + alert
  const onPreSubmit = async (agreements) => {
    if (is21kCompetitive) {
      const verdict = validateProof(agreements.proof_file);
      if (verdict !== true) {
        setError('proof_file', { type: 'manual', message: verdict });
        alert(`Invalid file: ${verdict}`);
        document
          .getElementById('proof-input')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    await onSubmit(agreements);
  };

  const closeResultDialog = () => {
    const currentStatus = resultDialog.status;

    setResultDialog((prev) => ({
      ...prev,
      open: false,
    }));

    if (currentStatus === 'success') {
      navigate(HOME_PATH, {
        replace: true,
      });
    }
  };

  if (registeraLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 8, sm: 5 },
        }}
      >
        <CircularProgress size="4rem" />
      </Box>
    );

  if (!values) {
    return (
      <Box
        sx={{
          maxWidth: '56rem',
          mx: 'auto',
          px: { xs: 2, md: 0 },
          py: 6,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            mb: 1,
            fontSize: 'clamp(1.625rem, 1.1vw + 1.2rem, 2.25rem)',
          }}
        >
          No data to summarize
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>
          Please fill the registration form first.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(registrationPath, { replace: true })}
        >
          Back to Form
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onPreSubmit)} // <-- use guard
      noValidate
      sx={{
        width: '100%',
        maxWidth: '56rem',
        mx: 'auto',
        px: { xs: 2, md: 0 },
        py: { xs: 5, sm: 3, md: 0 },
        mt: { xs: 3, md: 5, xl: 8 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            fontSize: 'clamp(1.625rem, 1.1vw + 1.2rem, 2.25rem)',
          }}
        >
          Registration Summary
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: 'clamp(1rem, 0.35vw + 0.9rem, 1.25rem)',
          }}
        >
          {show(values.race_category)}
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        <Section title="Registration Details">
          <RowGrid rows={rows} />

          {/* === Proof of Run (only 21K - competitive) === */}
          {is21kCompetitive && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
                }}
              >
                Proof of Run (required for 21K – Competitive)
              </Typography>

              <Controller
                name="proof_file"
                control={control}
                rules={{ validate: (f) => validateProof(f) }}
                render={({ field: { value, onChange } }) => (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'stretch',
                      flexWrap: 'wrap',
                      border: '1px solid #E5E7EB',
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    {/* Left: Choose file + filename + error right below */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flex: '1 1 280px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <input
                        id="proof-input"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          if (file) {
                            const verdict = validateProof(file);
                            if (verdict !== true) {
                              setError('proof_file', {
                                type: 'manual',
                                message: verdict,
                              });
                              alert(`Invalid file: ${verdict}`);
                              onChange(null); // don't store invalid file
                              return;
                            }
                            clearErrors('proof_file');
                          }
                          onChange(file);
                        }}
                      />
                      <label htmlFor="proof-input">
                        <Button variant="outlined" component="span">
                          Choose Image
                        </Button>
                      </label>

                      {/* filename */}
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {value
                          ? `${value.name} (${Math.ceil(
                              (value.size || 0) / 1024,
                            )} KB)`
                          : 'No file chosen'}
                      </Typography>

                      {/* error directly under filename */}
                      {errors.proof_file ? (
                        <FormHelperText error sx={{ m: 0 }}>
                          {errors.proof_file.message}
                        </FormHelperText>
                      ) : (
                        <FormHelperText sx={{ m: 0 }}>
                          Max file size: 2MB. Images only.
                        </FormHelperText>
                      )}
                    </Box>

                    {/* Right: Preview */}
                    <Box
                      sx={{
                        flex: '1 1 240px',
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: 2,
                        bgcolor: '#FAFAFA',
                      }}
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Proof preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 240,
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          No preview
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Box>
          )}

          {/* Divider antara detail dan agreement */}
          <Divider sx={{ my: 2.5 }} />
          {/* === Drop Bag? (sebelum Agreements) === */}
          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
              }}
            >
              Drop Bag?
            </Typography>
            <Controller
              name="drop_bag"
              control={control}
              rules={{ required: 'Please select one option.' }}
              render={({ field }) => (
                <>
                  <RadioGroup
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <FormControlLabel
                      value="use"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          I will use the <strong>Drop Bag Facility</strong> on
                          Race Day and comply to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc(
                                'Drop Bag Facility — Terms & Conditions',
                                DROP_BAG_TOC_TEXT,
                              )
                            }
                          >
                            terms and conditions
                          </Link>
                          .
                        </Typography>
                      }
                      sx={{
                        m: 0.25,
                        px: { xs: 1, md: 1.25 },
                        py: { xs: 0.5, md: 0.75 },
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                      }}
                    />
                    <FormControlLabel
                      value="not_use"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          I will not use the Drop Bag Facility
                        </Typography>
                      }
                      sx={{
                        m: 0.25,
                        px: { xs: 1, md: 1.25 },
                        py: { xs: 0.5, md: 0.75 },
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                  {errors.drop_bag && (
                    <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
                      {errors.drop_bag.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </Box>

          {/* Divider antara detail dan agreement */}
          <Divider sx={{ my: 2.5 }} />

          {/* === Agreements === */}
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
              }}
            >
              Agreements
            </Typography>

            <FormGroup sx={{ gap: 0.5 }}>
              <Controller
                name="agree_terms"
                control={control}
                rules={{ validate: (v) => v || 'You must accept the Terms.' }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I have read and agree to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Terms & Conditions', TERMS_TOC_TEXT)
                            }
                          >
                            Terms & Conditions
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_terms && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_terms.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_waiver"
                control={control}
                rules={{ validate: (v) => v || 'You must accept the waiver.' }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I acknowledge the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Event Waiver', WAIVER_TOC_TEXT)
                            }
                          >
                            Event Waiver
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_waiver && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_waiver.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_privacy"
                control={control}
                rules={{
                  validate: (v) => v || 'You must accept the Privacy Policy.',
                }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I agree to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Privacy Policy', PRIVACY_TOC_TEXT)
                            }
                          >
                            Privacy Policy
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_privacy && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_privacy.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_rules"
                control={control}
                rules={{
                  validate: (v) => v || 'You must acknowledge the race rules.',
                }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I agree to follow the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Race Rules', RULES_TOC_TEXT)
                            }
                          >
                            Race Rules
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_rules && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_rules.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormGroup>
          </Box>
        </Section>
      </Stack>

      <Box
        sx={{ mt: 2.5, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}
      >
        <Button type="button" variant="outlined" onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting || registeraLoading}
        >
          Confirm
        </Button>
      </Box>

      {/* Reusable ToC dialog */}
      <TocDialog
        open={tocModal.open}
        onClose={() => setTocModal((p) => ({ ...p, open: false }))}
        title={tocModal.title}
        text={tocModal.text}
      />
      <RegStatusDialog
        open={resultDialog.open}
        status={resultDialog.status}
        title={resultDialog.title}
        message={resultDialog.message}
        buttonLabel={
          resultDialog.status === 'success' ? 'Back to Event' : 'Close'
        }
        onClose={closeResultDialog}
      />
    </Box>
  );
}
